import React from "react";

import { adminTenancyStatusLabel } from "./TenantUnitGroup";

/**
 * Search, filters and page size — one row under the KPI cards.
 *
 * Every control here is server-side: `search`, `status` and `unit_id` are
 * query parameters of `GET /api/admin/tenancies`, and the page resets to the
 * first result when any of them changes. Filtering the rows already on screen
 * would silently mean "filter within page 1 of 6".
 *
 * The placeholder names what the server actually matches — reference number,
 * tenant name and e-mail, unit title/short id, room name. It deliberately
 * does not promise phone search, which that endpoint does not do.
 */

/**
 * The VALUES are DISPLAY statuses — the same words each row's badge shows, and
 * the same buckets the KPI cards count. They go on the wire as `?status=`.
 *
 * They used to be the stored `tenancies.status` column, and that was the bug:
 * a contract stored `reserved` whose move-in has passed renders "Aktiv", so
 * filtering "Aktiv" hid it, while "Gekündigt" could not be offered at all
 * because no stored value spells it. Since Slice 6A the server filters by the
 * derived status, so this list can finally say what the table says.
 *
 * The LABELS come from `adminTenancyStatusLabel`, the same function the badges
 * use, rather than being written out again here — that is what keeps the
 * filter reading "Vor Einzug storniert" when the row does, instead of the two
 * drifting to "Storniert" and back.
 */
const STATUS_VALUES = ["active", "reserved", "notice_given", "ended", "cancelled"];

const STATUS_OPTIONS = [
  { value: "", label: "Alle Status" },
  ...STATUS_VALUES.map((value) => ({
    value,
    label: adminTenancyStatusLabel(value),
  })),
];

const PAGE_SIZES = [25, 50, 100, 200];

const CONTROL =
  "h-[34px] rounded-[7px] border border-border bg-card px-[10px] text-[13px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

export default function TenantOverviewToolbar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  unitId,
  onUnitChange,
  unitOptions,
  pageSize,
  onPageSizeChange,
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      <div className="relative min-w-0 flex-1 sm:min-w-[260px]">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground"
        >
          ⌕
        </span>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Suche nach Name, E-Mail, Referenz, Unit oder Zimmer"
          aria-label="Mietverhältnisse suchen"
          className={`${CONTROL} w-full pl-[28px]`}
        />
      </div>

      <select
        value={status}
        onChange={(e) => onStatusChange(e.target.value)}
        aria-label="Status filtern"
        className={`${CONTROL} cursor-pointer`}
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.value || "all"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <select
        value={unitId}
        onChange={(e) => onUnitChange(e.target.value)}
        aria-label="Unit filtern"
        className={`${CONTROL} max-w-[220px] cursor-pointer`}
      >
        <option value="">Alle Units</option>
        {(unitOptions || []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.label}
          </option>
        ))}
      </select>

      <select
        value={String(pageSize)}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        aria-label="Einträge pro Seite"
        className={`${CONTROL} cursor-pointer`}
      >
        {PAGE_SIZES.map((n) => (
          <option key={n} value={n}>
            {n} pro Seite
          </option>
        ))}
      </select>
    </div>
  );
}
